import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { OptionsList } from 'gung-common';
import { Customer, CustomerService, MetadataService } from 'gung-standard';
import { DeliveryLocationsService, JeevesDeliveryLocation } from 'gung-standard-jeeves';
import { first } from 'rxjs';

@Component({
  selector: 'app-hl-display-add-edit-delivery-location-modal',
  templateUrl: './hl-display-add-edit-delivery-location-modal.component.html',
  styleUrl: './hl-display-add-edit-delivery-location-modal.component.scss',
  standalone: false
})
export class HlDisplayAddEditDeliveryLocationModalComponent implements OnInit {
  public selectedCustomerId: string;
  public selectedCustomer: Customer;
  public deliveryLocation: JeevesDeliveryLocation;
  public isEdit: boolean = false;

  public deliveryLocationForm: FormGroup;
  public countryOptions: OptionsList[] = [];
  public companyTypeOptions: OptionsList[] = [];

  creating: boolean = false;

  constructor(
    protected activeModal: NgbActiveModal,
    protected customerService: CustomerService,
    protected metadataService: MetadataService,
    protected translateService: TranslateService,
    protected deliveryLocationService: DeliveryLocationsService
  ) {}

  ngOnInit(): void {
    if (!this.selectedCustomerId) {
      throw new Error('No customer id provided');
    }
    if (!!this.deliveryLocation) {
      this.isEdit = true;
    }

    this.customerService
      .getCustomer(this.selectedCustomerId)
      .pipe(first())
      .subscribe(customer => {
        this.selectedCustomer = customer;
        this.initForm();
      });
  }

  protected initForm(): void {
    this.deliveryLocationForm = new FormGroup({
      customerNameShow: new FormControl({
        value: `${this.selectedCustomer.name} (${this.selectedCustomer.id})`,
        disabled: true
      }),
      companyName: new FormControl(this.deliveryLocation?.name || '', {}),
      address: new FormControl(this.deliveryLocation?.extra?.fr?.ftgpostadr1 || '', {}),
      street: new FormControl(this.deliveryLocation?.extra?.fr?.ftgpostadr2 || '', {}),
      city: new FormControl(this.deliveryLocation?.extra?.fr?.ftgpostadr3 || '', {}),
      postalCode: new FormControl(this.deliveryLocation?.extra?.fr?.ftgpostnr || '', {}),
      country: new FormControl(this.deliveryLocation?.extra?.fr?.landskod || '', {}),
      companyType: new FormControl(this.deliveryLocation?.extra?.fr?.companytype || 'EXTERNAL_CUSTOMER', {}),
      eanNumber: new FormControl(this.deliveryLocation?.extra?.fr.eanloccode || '', {})
    });

    this.countryOptions = this.metadataService.getMetadataTableList('xr').map(m => {
      return { id: m.landskod, name: m.land };
    });

    this.companyTypeOptions = (this.metadataService.getMetadataTableList('') || []).map(m => {
      return { id: '', name: '' };
    });

    this.companyTypeOptions.push({ id: 'EXTERNAL_CUSTOMER', name: this.translateService.instant('EXTERNAL_CUSTOMER') });
  }

  protected save(): void {
    this.creating = true;

    let formRawValue = this.deliveryLocationForm.getRawValue();
    delete formRawValue.customerNameShow;

    // Map form values to match the model
    const mappedValues: HLDisplayDeliveryLocation = {
      companyName: formRawValue.companyName,
      address: formRawValue.address,
      street: formRawValue.street,
      city: formRawValue.city,
      postalCode: formRawValue.postalCode,
      country: formRawValue.country,
      companyType: formRawValue.companyType,
      eanNumber: formRawValue.eanNumber
    };

    let result = this.deliveryLocation;

    if (!this.isEdit) {
      result = {
        ...result,
        customerId: this.selectedCustomer.id,
        name: mappedValues.companyName,
        extra: {
          companyName: mappedValues.companyName,
          fr: {
            ftgpostadr1: mappedValues.address,
            ftgpostadr2: mappedValues.street,
            ftgpostadr3: mappedValues.city,
            ftgpostnr: mappedValues.postalCode,
            landskod: mappedValues.country,
            companytype: mappedValues.companyType,
            eanloccode: mappedValues.eanNumber
          }
        }
      } as JeevesDeliveryLocation;
    }

    this.deliveryLocationService.createNewCustomerDeliveryLocation(result).subscribe(() => {
      this.creating = false;
      this.activeModal.close(true);
    });
  }

  protected close(): void {
    this.activeModal.dismiss();
  }
}

export interface HLDisplayDeliveryLocation {
  companyName: string;
  address: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  companyType: string;
  eanNumber: number;
}
